import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { GuiasRelacionadas } from "@/components/GuiasRelacionadas";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";
import hero640Asset from "../assets/hero-640.webp.asset.json";

const srcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;
const SITE = "https://elpajardebenito.es";
const URL_CANON = `${SITE}/casa-rural-pueblos-negros`;
const OG_IMAGE = `${SITE}${hero1280Asset.url}`;

const pueblos = [
  { t: "Valverde de los Arroyos", d: "15 min. Uno de los Pueblos más Bonitos de España, con la cascada de la Chorrera de Despeñalagua a 45 min de paseo." },
  { t: "Majaelrayo", d: "35 min. Al pie del Pico Ocejón, la referencia clásica de la arquitectura negra." },
  { t: "Campillo de Ranas", d: "35 min. Conjunto de pizarra impecable y punto de partida de varias sendas." },
  { t: "Roblelacasa y Campillejo", d: "30 min. Aldeas pequeñas y la ruta del Puente del Matallana." },
  { t: "Umbralejo", d: "25 min. Pueblo recuperado como aula de naturaleza, visitable a pie." },
  { t: "Almiruete", d: "30 min. Famoso por sus botargas y mascaritas de carnaval." },
];

const faqs = [
  { q: "¿Dónde dormir para visitar los pueblos negros?", a: "El Ordial es una base excelente: está dentro de la comarca serrana y todos los pueblos negros principales quedan a menos de 35 minutos en coche. Dormir aquí evita hacer el trayecto desde Guadalajara capital o Madrid cada día." },
  { q: "¿Cuántos días hacen falta para ver los pueblos negros?", a: "Con un fin de semana se ven bien tres o cuatro pueblos con calma y una ruta a pie. Si quieres añadir el Hayedo de Tejera Negra o subir al Ocejón o al Alto Rey, mejor tres días." },
  { q: "¿Por qué se llaman pueblos negros?", a: "Por la pizarra oscura con la que están construidos: muros, tejados y cercados. Es la arquitectura tradicional de la zona, adaptada a la piedra que hay en la sierra." },
  { q: "¿Se pueden recorrer andando entre ellos?", a: "Sí, existe la Senda de los Pueblos Negros, una ruta de varias etapas que los enlaza. También hay tramos cortos, como el de Campillo de Ranas a Roblelacasa, que se hacen en una mañana." },
  { q: "¿Cuál es la mejor época del año?", a: "El otoño es espectacular por el color del hayedo y los robledales. La primavera es ideal para la cascada de Despeñalagua, que baja con más caudal. El invierno tiene su encanto con la chimenea encendida." },
];

export const Route = createFileRoute("/casa-rural-pueblos-negros")({
  head: () => ({
    meta: [
      { title: "Casa rural Pueblos Negros Guadalajara desde 55 € | El Pajar de Benito" },
      { name: "description", content: "Dónde dormir en los Pueblos Negros: 6 habitaciones con baño privado desde 55 €/noche o casa completa hasta 14 personas. A 35 min de Valverde y Majaelrayo. Reserva directa." },
      { property: "og:title", content: "Casa rural Pueblos Negros Guadalajara desde 55 €" },
      { property: "og:description", content: "6 habitaciones con baño privado o casa completa para 14. Base para Valverde de los Arroyos, Majaelrayo y Campillo de Ranas. Reserva directa sin comisiones." },
      { property: "og:url", content: URL_CANON },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL_CANON }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Casa rural en los pueblos negros", to: "/casa-rural-pueblos-negros" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Casa rural en los pueblos negros" }]} />
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={hero1280Asset.url} srcSet={srcSet} sizes="100vw" alt="Sierra de los pueblos negros de Guadalajara" width={1920} height={1280} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-4 pb-16 md:px-6">
          <p className="text-xs uppercase tracking-[0.28em] text-white/80">Arquitectura negra · Guadalajara</p>
          <h1 className="mt-3 font-serif text-4xl text-white md:text-5xl">Casa rural en los Pueblos Negros de Guadalajara</h1>
          <p className="mt-4 max-w-2xl text-white/90">Duerme dentro de la comarca: todos los pueblos de pizarra a menos de 35 minutos.</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Dormir dentro de la comarca, no de paso</h2>
        <p className="mt-4 text-muted-foreground">
          La mayoría de quien visita los <strong>pueblos negros de Guadalajara</strong> lo hace en una excursión de un día desde Madrid: cuatro horas de coche, dos pueblos vistos con prisa y vuelta a casa. Se pierde lo mejor, que es el silencio de la sierra al atardecer y el cielo de noche.
        </p>
        <p className="mt-4 text-muted-foreground">
          <strong>El Pajar de Benito</strong> está en El Ordial, un pueblo pequeño de la Sierra del Alto Rey, dentro de la misma comarca serrana. Desde aquí sales por la mañana, recorres dos o tres pueblos de pizarra sin agobios, comes en la zona y vuelves a la casa a encender la chimenea. Es la diferencia entre visitar los pueblos negros y vivirlos.
        </p>
        <p className="mt-4 text-muted-foreground">
          La casa se alquila completa (hasta 14 plazas) o por habitaciones dobles con baño privado, así que funciona igual de bien para una pareja que para un grupo de amigos o una familia grande.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Los pueblos negros, uno a uno</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {pueblos.map((p) => (
            <div key={p.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg">{p.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Los tiempos son en coche desde El Ordial por carreteras de montaña; conviene no ir con prisa, sobre todo en invierno.
        </p>
      </section>

      <section className="bg-muted/40">
        <div className="mx-auto max-w-4xl px-4 py-16 md:px-6">
          <h2 className="font-serif text-3xl text-foreground">Un fin de semana bien repartido</h2>
          <ol className="mt-6 list-decimal space-y-3 pl-6 text-muted-foreground">
            <li><strong>Viernes tarde:</strong> llegada, chimenea y cena en la casa. Si el cielo está despejado, salir al patio a mirar estrellas.</li>
            <li><strong>Sábado:</strong> Valverde de los Arroyos y la cascada de Despeñalagua por la mañana; por la tarde, Majaelrayo y Campillo de Ranas.</li>
            <li><strong>Domingo:</strong> subida al Pico Alto Rey desde Bustares, o visita al Hayedo de Tejera Negra si has reservado plaza de parking.</li>
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Preguntas frecuentes</h2>
        <div className="mt-8 space-y-6">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-foreground">{f.q}</h3>
              <p className="mt-2 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <GuiasRelacionadas slugs={["arquitectura-negra", "que-ver-sierra-norte-guadalajara", "guia-hayedo-tejera-negra"]} />

      <section className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">Reserva tu base en la sierra</h2>
        <p className="mt-4 text-muted-foreground">Consulta fechas y precios en tiempo real, sin intermediarios.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/reserva" className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Ver disponibilidad</Link>
          <Link to="/entorno" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-muted">Explorar el entorno</Link>
        </div>
      </section>
    </>
  );
}
